import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import { fetchAirshowReviewArtifact } from "./airshowReviewApi";
import { computeBboxAlignment, decodeAirshowGltf } from "./airshowComparisonGeometry";
import type {
  AirshowAlignmentMode,
  AirshowBoundingBox,
  AirshowCameraEvidence,
  AirshowGeometryMode,
  AirshowManualAlignment,
  AirshowReviewLayout,
} from "./airshowReviewContract";

export type AirshowCameraPreset = "front" | "side" | "top" | "isometric" | "reset";

export interface AirshowComparisonSide {
  modelId: string;
  status: "loading" | "available" | "unavailable";
  geometryMode: AirshowGeometryMode;
  modelUrl: string | null;
  bbox?: AirshowBoundingBox | null;
  fidelityCode: string;
  fidelityLabel: string;
  unavailableReason?: string;
}

interface ViewerProps {
  reference: AirshowComparisonSide;
  loom: AirshowComparisonSide;
  layout: AirshowReviewLayout;
  overlayOpacity: number;
  visibility: { reference: boolean; loom: boolean };
  alignmentMode: AirshowAlignmentMode;
  manualAlignment: AirshowManualAlignment;
  cameraCommand: { id: number; preset: AirshowCameraPreset };
  onCameraEvidence: (evidence: AirshowCameraEvidence) => void;
  onResolvedAlignment: (alignment: AirshowManualAlignment) => void;
}

interface SideRenderState {
  state: "loading" | "ready" | "unavailable";
  meshCount: number;
  error: string | null;
}

interface ViewerRuntime {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  referenceGroup: THREE.Group;
  loomGroup: THREE.Group;
  referenceBox: THREE.Box3 | null;
  loomBox: THREE.Box3 | null;
  cameraRevision: number;
  raf: number;
}

const EMPTY_CAMERA: AirshowCameraEvidence = {
  signature: "camera|pending",
  revision: 0,
};

function disposeGroup(group: THREE.Group): void {
  for (const child of [...group.children]) {
    group.remove(child);
    if (!(child instanceof THREE.Mesh)) continue;
    child.geometry.dispose();
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => material.dispose());
  }
}

function boxTuple(box: THREE.Box3): AirshowBoundingBox {
  return [[box.min.x, box.min.y, box.min.z], [box.max.x, box.max.y, box.max.z]];
}

function applyGroupAlignment(group: THREE.Group, alignment: AirshowManualAlignment): void {
  group.position.set(...alignment.translation);
  group.rotation.set(...alignment.rotationDegrees.map(THREE.MathUtils.degToRad) as [number, number, number]);
  group.scale.setScalar(alignment.scale);
  group.updateMatrixWorld(true);
}

function resolveAlignment(
  runtime: ViewerRuntime,
  mode: AirshowAlignmentMode,
  manual: AirshowManualAlignment,
  referenceEvidenceBox: AirshowBoundingBox | null | undefined,
): AirshowManualAlignment {
  if (mode === "manual") return manual;
  if (mode === "bbox-center" && runtime.loomBox && (referenceEvidenceBox || runtime.referenceBox)) {
    return computeBboxAlignment(referenceEvidenceBox ?? boxTuple(runtime.referenceBox!), boxTuple(runtime.loomBox));
  }
  return { translation: [0, 0, 0], rotationDegrees: [0, 0, 0], scale: 1 };
}

function sceneBox(runtime: ViewerRuntime): THREE.Box3 | null {
  const union = new THREE.Box3();
  let hasGeometry = false;
  for (const group of [runtime.referenceGroup, runtime.loomGroup]) {
    if (!group.children.length) continue;
    union.union(new THREE.Box3().setFromObject(group));
    hasGeometry = true;
  }
  return hasGeometry ? union : null;
}

function cameraSignature(runtime: ViewerRuntime): string {
  const values = [
    runtime.camera.position.x, runtime.camera.position.y, runtime.camera.position.z,
    runtime.controls.target.x, runtime.controls.target.y, runtime.controls.target.z,
  ].map((value) => value.toFixed(4));
  return `camera|p=${values.slice(0, 3).join(",")}|t=${values.slice(3).join(",")}`;
}

function placeCamera(runtime: ViewerRuntime, preset: AirshowCameraPreset): void {
  const box = sceneBox(runtime) ?? new THREE.Box3(
    new THREE.Vector3(-1, -1, -1), new THREE.Vector3(1, 1, 1),
  );
  const center = box.getCenter(new THREE.Vector3());
  const radius = Math.max(box.getSize(new THREE.Vector3()).length() * 0.5, 0.5);
  const directions: Record<AirshowCameraPreset, THREE.Vector3> = {
    front: new THREE.Vector3(-1, 0, 0),
    side: new THREE.Vector3(0, -1, 0),
    top: new THREE.Vector3(0, 0, 1),
    isometric: new THREE.Vector3(-0.75, -0.9, 0.55).normalize(),
    reset: new THREE.Vector3(-0.75, -0.9, 0.55).normalize(),
  };
  runtime.camera.up.set(preset === "top" ? 1 : 0, 0, preset === "top" ? 0 : 1);
  runtime.controls.target.copy(center);
  runtime.camera.position.copy(center).add(directions[preset].multiplyScalar(radius * 2.8));
  runtime.camera.near = Math.max(radius / 100, 0.001);
  runtime.camera.far = Math.max(radius * 100, 100);
  runtime.camera.updateProjectionMatrix();
  runtime.controls.update();
}

function updateMaterials(runtime: ViewerRuntime, layout: AirshowReviewLayout, opacity: number): void {
  runtime.renderer.domElement.dataset.referenceMaterialOpacity = (layout === "overlay" ? opacity : 1).toFixed(2);
  runtime.referenceGroup.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const material = child.material as THREE.MeshStandardMaterial;
    material.opacity = layout === "overlay" ? opacity : 1;
    material.transparent = layout === "overlay";
    material.depthWrite = layout !== "overlay";
    material.wireframe = layout === "overlay";
    child.renderOrder = layout === "overlay" ? 2 : 0;
  });
}

function viewportState(side: AirshowComparisonSide, state: SideRenderState): SideRenderState["state"] {
  if (side.status === "loading") return "loading";
  if (side.status === "unavailable") return "unavailable";
  return state.state;
}

export function AirshowComparisonViewer({
  reference, loom, layout, overlayOpacity, visibility, alignmentMode, manualAlignment,
  cameraCommand, onCameraEvidence, onResolvedAlignment,
}: ViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const runtimeRef = useRef<ViewerRuntime | null>(null);
  const settingsRef = useRef({ layout, overlayOpacity, visibility });
  const callbacksRef = useRef({ onCameraEvidence, onResolvedAlignment });
  const alignmentRef = useRef({ alignmentMode, manualAlignment, referenceBox: reference.bbox });
  const [camera, setCamera] = useState<AirshowCameraEvidence>(EMPTY_CAMERA);
  const [referenceRender, setReferenceRender] = useState<SideRenderState>({ state: "loading", meshCount: 0, error: null });
  const [loomRender, setLoomRender] = useState<SideRenderState>({ state: "loading", meshCount: 0, error: null });
  settingsRef.current = { layout, overlayOpacity, visibility };
  callbacksRef.current = { onCameraEvidence, onResolvedAlignment };
  alignmentRef.current = { alignmentMode, manualAlignment, referenceBox: reference.bbox };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x11171d, 1);
    const scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xffffff, 0x24313b, 1.6));
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(-2, -3, 5);
    scene.add(key);
    const referenceGroup = new THREE.Group();
    const loomGroup = new THREE.Group();
    scene.add(referenceGroup, loomGroup);
    const cameraObject = new THREE.PerspectiveCamera(42, 1, 0.01, 5000);
    cameraObject.up.set(0, 0, 1);
    cameraObject.position.set(-4, -5, 3);
    const controls = new OrbitControls(cameraObject, canvas);
    controls.enableDamping = true;
    const runtime: ViewerRuntime = {
      renderer, scene, camera: cameraObject, controls, referenceGroup, loomGroup,
      referenceBox: null, loomBox: null, cameraRevision: 0, raf: 0,
    };
    runtimeRef.current = runtime;
    const emitCamera = () => {
      const evidence = { signature: cameraSignature(runtime), revision: runtime.cameraRevision += 1 };
      setCamera(evidence);
      callbacksRef.current.onCameraEvidence(evidence);
    };
    controls.addEventListener("change", emitCamera);
    const resize = () => {
      const width = Math.max(canvas.clientWidth, 1);
      const height = Math.max(canvas.clientHeight, 1);
      renderer.setSize(width, height, false);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();
    const draw = () => {
      controls.update();
      // Three applies renderer.pixelRatio inside setViewport/setScissor. Supplying the
      // backing-buffer dimensions here scales a second time and clips Retina displays.
      const width = Math.max(renderer.domElement.clientWidth, 1);
      const height = Math.max(renderer.domElement.clientHeight, 1);
      const config = settingsRef.current;
      renderer.setScissorTest(true);
      if (config.layout === "split") {
        const half = Math.floor(width / 2);
        cameraObject.aspect = Math.max(half, 1) / Math.max(height, 1);
        cameraObject.updateProjectionMatrix();
        referenceGroup.visible = config.visibility.reference;
        loomGroup.visible = false;
        renderer.setViewport(0, 0, half, height);
        renderer.setScissor(0, 0, half, height);
        renderer.render(scene, cameraObject);
        referenceGroup.visible = false;
        loomGroup.visible = config.visibility.loom;
        renderer.setViewport(half, 0, width - half, height);
        renderer.setScissor(half, 0, width - half, height);
        renderer.render(scene, cameraObject);
      } else {
        cameraObject.aspect = Math.max(width, 1) / Math.max(height, 1);
        cameraObject.updateProjectionMatrix();
        referenceGroup.visible = config.visibility.reference;
        loomGroup.visible = config.visibility.loom;
        renderer.setViewport(0, 0, width, height);
        renderer.setScissor(0, 0, width, height);
        renderer.render(scene, cameraObject);
      }
      runtime.raf = requestAnimationFrame(draw);
    };
    placeCamera(runtime, "isometric");
    draw();
    return () => {
      cancelAnimationFrame(runtime.raf);
      observer.disconnect();
      controls.removeEventListener("change", emitCamera);
      controls.dispose();
      disposeGroup(referenceGroup);
      disposeGroup(loomGroup);
      renderer.dispose();
      runtimeRef.current = null;
    };
  }, []);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    const controller = new AbortController();
    const load = async (
      side: AirshowComparisonSide,
      group: THREE.Group,
      color: number,
      setRender: (state: SideRenderState) => void,
      setBox: (box: THREE.Box3 | null) => void,
    ) => {
      disposeGroup(group);
      setBox(null);
      if (side.status !== "available" || !side.modelUrl) {
        setRender({ state: side.status === "loading" ? "loading" : "unavailable", meshCount: 0, error: null });
        return;
      }
      setRender({ state: "loading", meshCount: 0, error: null });
      try {
        const parts = decodeAirshowGltf(await fetchAirshowReviewArtifact(side.modelUrl, controller.signal));
        if (controller.signal.aborted) return;
        const sourceBox = new THREE.Box3();
        parts.forEach((part) => {
          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute("position", new THREE.BufferAttribute(part.positions, 3));
          geometry.setIndex(new THREE.BufferAttribute(part.indices, 1));
          geometry.computeVertexNormals();
          geometry.computeBoundingBox();
          if (geometry.boundingBox) sourceBox.union(geometry.boundingBox);
          const material = new THREE.MeshStandardMaterial({
            color, roughness: 0.72, metalness: 0.04, side: THREE.DoubleSide,
          });
          const mesh = new THREE.Mesh(geometry, material);
          mesh.name = part.name;
          group.add(mesh);
        });
        setBox(sourceBox);
        setRender({ state: "ready", meshCount: parts.length, error: null });
        const config = alignmentRef.current;
        const resolved = resolveAlignment(runtime, config.alignmentMode, config.manualAlignment, config.referenceBox);
        applyGroupAlignment(runtime.loomGroup, resolved);
        callbacksRef.current.onResolvedAlignment(resolved);
        updateMaterials(runtime, settingsRef.current.layout, settingsRef.current.overlayOpacity);
        placeCamera(runtime, "isometric");
      } catch (error) {
        if (controller.signal.aborted || (error instanceof Error && error.name === "AbortError")) return;
        disposeGroup(group);
        setBox(null);
        setRender({
          state: "unavailable", meshCount: 0,
          error: error instanceof Error ? error.message : "Model artifact is unavailable",
        });
      }
    };
    void load(reference, runtime.referenceGroup, 0xf2ad55, setReferenceRender, (box) => { runtime.referenceBox = box; });
    void load(loom, runtime.loomGroup, 0x45b7d1, setLoomRender, (box) => { runtime.loomBox = box; });
    return () => controller.abort();
  }, [reference.modelId, reference.modelUrl, reference.status, loom.modelId, loom.modelUrl, loom.status]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    const resolved = resolveAlignment(runtime, alignmentMode, manualAlignment, reference.bbox);
    applyGroupAlignment(runtime.loomGroup, resolved);
    onResolvedAlignment(resolved);
    updateMaterials(runtime, layout, overlayOpacity);
  }, [alignmentMode, layout, manualAlignment, onResolvedAlignment, overlayOpacity, reference.bbox]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime) placeCamera(runtime, cameraCommand.preset);
  }, [cameraCommand]);

  const referenceState = viewportState(reference, referenceRender);
  const loomState = viewportState(loom, loomRender);
  return (
    <div className="airshow-review-viewer">
      <canvas aria-label="Synchronized OpenVSP comparison" data-testid="openvsp-comparison-canvas" ref={canvasRef} />
      <div
        className="airshow-review-viewport is-reference"
        data-camera-view-signature={camera.signature}
        data-display-representation="tessellated"
        data-geometry-mode={reference.geometryMode}
        data-model-id={reference.modelId}
        data-render-state={referenceState}
        data-rendered-mesh-count={referenceState === "ready" ? referenceRender.meshCount : 0}
        data-testid="openvsp-viewport-left"
      >
        <header><span>OpenVSP reference</span><strong data-fidelity={reference.fidelityCode} data-testid="openvsp-fidelity-left">{reference.fidelityLabel}</strong></header>
        {referenceState === "unavailable" ? <p data-testid="openvsp-unavailable-left">{referenceRender.error ?? reference.unavailableReason ?? "Reference unavailable"}</p> : null}
      </div>
      <div
        className="airshow-review-viewport is-loom"
        data-camera-view-signature={camera.signature}
        data-display-representation="tessellated"
        data-geometry-mode={loom.geometryMode}
        data-model-id={loom.modelId}
        data-render-state={loomState}
        data-rendered-mesh-count={loomState === "ready" ? loomRender.meshCount : 0}
        data-testid="openvsp-viewport-right"
      >
        <header><span>Loom OpenVSP adapter</span><strong data-fidelity={loom.fidelityCode} data-testid="openvsp-fidelity-right">{loom.fidelityLabel}</strong></header>
        {loomState === "unavailable" ? <p data-testid="openvsp-unavailable-right">{loomRender.error ?? loom.unavailableReason ?? "Loom candidate unavailable"}</p> : null}
      </div>
    </div>
  );
}
